import { apiFetch } from "./api";
import { requestLock } from "./lockBus";

// iOS Safari's <video>/<audio> elements load their src through a separate
// media pipeline that doesn't reliably send the session cookie (not a
// SameSite policy thing - same-origin requests aren't affected by that -
// the cookie just isn't attached at all in some circumstances). This mints
// a short-lived token once per session and appends it to every
// /assets/media, /assets/thumbs, /assets/music URL so those routes don't
// depend on the cookie actually arriving. See server.js's
// requireMediaAuth() for the server side of this.

let token: string | null = null;

// Long-idle audit finding: this used to just `return` on any non-ok
// response, silently leaving the OLD (soon-to-expire) token in place
// forever. Since mediaTokens are minted with a fixed, non-rolling expiry
// server-side (server.js's validMediaTokenUserId never extends it), and
// this function is normally only called every 45 minutes
// (AuthGate.tsx's MEDIA_TOKEN_REFRESH_MS) - an interval mobile browsers
// can suspend while backgrounded - a stale token could silently keep being
// attached to every image/video/audio URL long after the server stopped
// honoring it, with no fetch()-based 401 anywhere for the existing
// API-wrapper error handling to ever catch (these are raw <img>/<video>
// src loads, not apiFetch calls). A 401 here specifically means the
// SESSION itself (not just this token) is gone - requireMediaAuth() falls
// back to full session-cookie auth whenever the query token doesn't
// resolve, so this is as authoritative a signal as any other 401.
export async function refreshMediaToken(): Promise<void> {
  try {
    const res = await apiFetch("/api/media-token");
    if (res.status === 401) {
      token = null;
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const data = await res.json();
    if (typeof data.token === "string") token = data.token;
  } catch {
    // Non-fatal - mediaUrl() just omits the token, and normal cookie-based
    // requests (everything except iOS video/audio) keep working regardless.
  }
}

export function clearMediaToken(): void {
  token = null;
}

export function mediaUrl(path: string): string {
  if (!token) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}t=${encodeURIComponent(token)}`;
}
